import { Link } from 'react-router-dom'
import {
  useAtRiskProcedures,
  useFormsSummary,
  usePatientsSummary,
  useProcedurePipeline,
  useProceduresSummary,
  useUpcomingProcedures,
} from '@/hooks/useDashboardData'
import {
  atRiskText,
  deltaOf,
  openOfType,
  previousPeriodLabel,
  procedureTypeLabel,
  runHref,
  runTitle,
  shortDayLabel,
} from '@/lib/dashboardWidgetFormat'
import { dashboardTimeWindow, previousTimeWindow } from '@/lib/dashboardLayouts'
import { PROCEDURE_TYPE_COLOR } from '@/lib/procedureTypeColors'
import { windowLabel } from '@/lib/reportRange'
import { cn } from '@/lib/utils'
import type { DashboardRunRef } from '@/api/dashboardApi'
import type { ProcedureType } from '@/types/cgt'
import type { WidgetBodyProps } from '../builtWidgets'
import { DeltaBadge, Sparkline, STATUS_COLORS } from '../charts'
import { PartsView, SeriesView, dailyRows, partsKind, seriesKind, type Part } from '../views'
import { WidgetQueryState, WidgetStat } from '../WidgetCard'

// ─── Transplant, Patients & Forms widgets (z8nrz7eqyj) ───────────────────────
//
// Every number comes from the dashboard endpoints, which reuse the Reports'
// own computations (open = report 1.5, started = 1.1, completed = 1.2). Each
// widget draws itself in the view the user chose (see `widgetViews`).

const typeColor = (type: ProcedureType | 'unknown') =>
  type === 'unknown' ? '#9ca3af' : PROCEDURE_TYPE_COLOR[type]

const typeParts = (rows: { type: ProcedureType | 'unknown'; n: number }[]): Part[] =>
  rows.filter(r => r.n > 0).map(r => ({ label: procedureTypeLabel(r.type), value: r.n, color: typeColor(r.type) }))

export function TransplantsInProgressWidget({ view }: WidgetBodyProps) {
  // Open "now": the window ending today, whatever period the dashboard shows.
  const q = useProceduresSummary(dashboardTimeWindow('today', new Date()).query)
  const parts = typeParts((q.data?.open.byType ?? []).map(t => ({ type: t.type, n: t.count })))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view !== 'number' && parts.length === 0}
      emptyText='No procedures are open.'
      onRetry={() => void q.refetch()}
    >
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={openOfType(q.data.open.byType, 'Transplant')}
            href='/procedures'
            caption='Transplant procedures open now'
            footer={`${q.data.open.total} open across all procedure types`}
          />
        ) : (
          <PartsView view={partsKind(view)} parts={parts} totalLabel='open' />
        ))}
    </WidgetQueryState>
  )
}

export function CompletedInPeriodWidget({ time, view }: WidgetBodyProps) {
  const q = useProceduresSummary(time.query)
  const prev = useProceduresSummary(previousTimeWindow(time).query)
  const points = (q.data?.trend ?? []).map(d => ({ day: d.day, count: d.completed }))
  const delta = q.data && prev.data && (
    <DeltaBadge delta={deltaOf(q.data.completed.total, prev.data.completed.total)} period={previousPeriodLabel(time.id)} />
  )
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.completed.total}
            caption={`Procedures completed · ${windowLabel(time.window)}`}
            delta={delta}
            trend={<Sparkline points={points} label='completed' color={STATUS_COLORS.good} />}
            footer={
              q.data.completed.undated > 0
                ? `${q.data.completed.undated} more complete with no completion date`
                : `${q.data.started.total} started in the same period`
            }
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='flex items-center gap-2 text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.completed.total}</span> completed {delta}
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(points)}
              series={[{ key: 'count', label: 'Completed', color: STATUS_COLORS.good }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function ThroughputWidget({ time, view }: WidgetBodyProps) {
  const q = useProceduresSummary(time.query)
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.started.total === 0 && q.data.completed.total === 0}
      emptyText='No procedures started or completed in this period.'
      onRetry={() => void q.refetch()}
    >
      {q.data && (
        <div className='flex flex-1 flex-col gap-1'>
          <p className='text-xs text-gray-500'>
            <span className='font-semibold text-gray-800'>{q.data.started.total}</span> started ·{' '}
            <span className='font-semibold text-gray-800'>{q.data.completed.total}</span> completed ·{' '}
            {windowLabel(time.window)}
          </p>
          <SeriesView
            view={seriesKind(view, 'lines')}
            rows={q.data.trend}
            height={170}
            series={[
              { key: 'started', label: 'Started', color: STATUS_COLORS.accent },
              { key: 'completed', label: 'Completed', color: STATUS_COLORS.good },
            ]}
          />
        </div>
      )}
    </WidgetQueryState>
  )
}

export function ProceduresByTypeWidget({ time, view }: WidgetBodyProps) {
  const q = useProceduresSummary(time.query)
  const parts = typeParts((q.data?.started.byType ?? []).map(t => ({ type: t.type, n: t.total })))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={parts.length === 0}
      emptyText='No procedures started in this period.'
      onRetry={() => void q.refetch()}
    >
      <p className='mb-1 text-[11px] text-gray-400'>Started · {windowLabel(time.window)}</p>
      <PartsView view={partsKind(view)} parts={parts} totalLabel='started' />
    </WidgetQueryState>
  )
}

/** Stages read in workflow order, so they take one hue, light to dark. */
const STAGE_RAMP = ['#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af']

export function PipelineWidget({ size, view }: WidgetBodyProps) {
  const q = useProcedurePipeline()
  const stages = q.data?.stages ?? []
  const max = Math.max(1, ...stages.map(s => s.count))
  const parts: Part[] = stages.map((s, i) => ({
    label: s.name,
    value: s.count,
    color: STAGE_RAMP[Math.min(STAGE_RAMP.length - 1, Math.round((i / Math.max(1, stages.length - 1)) * (STAGE_RAMP.length - 1)))],
  }))
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.total === 0}
      emptyText='No transplant procedures are open.'
      onRetry={() => void q.refetch()}
    >
      {q.data && (
        <div className='flex flex-1 flex-col gap-2'>
          {view === 'stages' ? (
            <ol className={cn('flex gap-2', size === 'small' ? 'flex-col' : 'flex-row overflow-x-auto pb-1')}>
              {stages.map((s, i) => (
                <li
                  key={s.name}
                  className={cn(
                    'relative flex min-w-[104px] flex-1 flex-col justify-between rounded-lg border border-blue-100 bg-gradient-to-b from-blue-50 to-white px-3 py-2',
                    size === 'small' && 'flex-row items-center',
                  )}
                >
                  <p className='text-[11px] font-medium leading-tight text-gray-600'>
                    <span className='mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[9px] font-bold text-white'>
                      {i + 1}
                    </span>
                    {s.name}
                  </p>
                  <div className={cn(size === 'small' ? 'ml-2' : 'mt-2')}>
                    <p className='text-2xl font-bold tabular-nums text-gray-900'>{s.count}</p>
                    {size !== 'small' && (
                      <div className='mt-1 h-1 rounded-full bg-blue-100'>
                        <div className='h-1 rounded-full bg-blue-600' style={{ width: `${(s.count / max) * 100}%` }} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <PartsView view={partsKind(view)} parts={parts} totalLabel='Step' />
          )}
          <p className='text-[11px] text-gray-400'>
            {q.data.total} open transplant procedure{q.data.total === 1 ? '' : 's'}, by the step they are on
            {q.data.unplaced > 0 && ` · ${q.data.unplaced} with no step to place them on`}
          </p>
        </div>
      )}
    </WidgetQueryState>
  )
}

export function AtRiskWidget({ view }: WidgetBodyProps) {
  const q = useAtRiskProcedures()
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={!!q.data && q.data.overdue + q.data.dueSoon === 0}
      emptyText='Nothing is overdue or due in the next 48 hours.'
      onRetry={() => void q.refetch()}
    >
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.overdue}
            tone={q.data.overdue > 0 ? 'red' : 'blue'}
            href='/procedures'
            caption='Procedures past their due date'
            footer={`${q.data.dueSoon} more due within 48 hours`}
          />
        ) : view === 'donut' || view === 'bars' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='at risk'
            parts={[
              { label: 'Overdue', value: q.data.overdue, color: STATUS_COLORS.bad },
              { label: 'Due within 48h', value: q.data.dueSoon, color: '#f59e0b' },
            ]}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-2'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-red-600'>{q.data.overdue} overdue</span>
              {' · '}
              <span className='font-semibold text-amber-600'>{q.data.dueSoon} due in 48 hours</span>
            </p>
            <ul className='divide-y divide-gray-100'>
              {q.data.rows.map(row => (
                <RunRow
                  key={row.id}
                  run={row}
                  aside={
                    <span className={cn('font-medium', row.reason === 'overdue' ? 'text-red-600' : 'text-amber-600')}>
                      {atRiskText(row)}
                    </span>
                  }
                />
              ))}
            </ul>
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function UpcomingProceduresWidget({ size, view }: WidgetBodyProps) {
  const q = useUpcomingProcedures()
  const max = Math.max(1, ...(q.data?.byDay ?? []).map(d => d.count))
  const when = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  return (
    <WidgetQueryState
      isLoading={q.isLoading}
      error={q.error}
      isEmpty={view === 'list' && !!q.data && q.data.total === 0}
      emptyText='Nothing is scheduled to start in the next 7 days.'
      onRetry={() => void q.refetch()}
    >
      {q.data &&
        (view === 'columns' || view === 'trend' || view === 'table' ? (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.total}</span> scheduled in the next {q.data.days} days
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.byDay)}
              series={[{ key: 'count', label: 'Scheduled', color: STATUS_COLORS.accent }]}
            />
          </div>
        ) : view === 'list' ? (
          <ul className='divide-y divide-gray-100'>
            {q.data.rows.map(row => (
              <RunRow key={row.id} run={row} aside={when(row.scheduledDate)} />
            ))}
          </ul>
        ) : (
          <div className='flex flex-1 flex-col gap-3'>
            <WidgetStat value={q.data.total} href='/procedures/calendar' caption={`Scheduled to start in the next ${q.data.days} days`} />
            <div className='flex h-14 items-end gap-1' aria-label='Procedures per day'>
              {q.data.byDay.map(d => (
                <div key={d.day} className='flex flex-1 flex-col items-center gap-1' title={`${shortDayLabel(d.day)}: ${d.count}`}>
                  <div
                    className={cn('w-full rounded-t', d.count > 0 ? 'bg-blue-600' : 'bg-gray-100')}
                    style={{ height: `${Math.max(6, (d.count / max) * 40)}px` }}
                  />
                  <span className='text-[9px] text-gray-400'>{shortDayLabel(d.day).split(' ')[0]}</span>
                </div>
              ))}
            </div>
            {size !== 'small' && q.data.rows.length > 0 && (
              <ul className='divide-y divide-gray-100'>
                {q.data.rows.slice(0, 4).map(row => (
                  <RunRow key={row.id} run={row} aside={when(row.scheduledDate)} />
                ))}
              </ul>
            )}
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function NewPatientsWidget({ time, view }: WidgetBodyProps) {
  const q = usePatientsSummary(time.query)
  const prev = usePatientsSummary(previousTimeWindow(time).query)
  const delta = q.data && prev.data && (
    <DeltaBadge delta={deltaOf(q.data.newInPeriod, prev.data.newInPeriod, null)} period={previousPeriodLabel(time.id)} />
  )
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.newInPeriod}
            href='/patients'
            caption={`Patients registered · ${windowLabel(time.window)}`}
            delta={delta}
            trend={<Sparkline points={q.data.newByDay} label='registered' />}
            footer={`${q.data.total} patients in total`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='flex items-center gap-2 text-xs text-gray-500'>
              <span className='font-semibold text-gray-900'>{q.data.newInPeriod}</span> registered {delta}
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.newByDay)}
              series={[{ key: 'count', label: 'Registered', color: '#7c3aed' }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

export function ActivePatientsWidget({ time, view }: WidgetBodyProps) {
  const q = usePatientsSummary(time.query)
  const share = q.data && q.data.total > 0 ? Math.round((q.data.active / q.data.total) * 100) : 0
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view !== 'number' ? (
          <PartsView
            view={partsKind(view, 'donut')}
            totalLabel='patients'
            parts={[
              { label: 'In progress', value: q.data.active, color: STATUS_COLORS.accent },
              { label: 'No open procedure', value: Math.max(0, q.data.total - q.data.active), color: STATUS_COLORS.accentLighter },
            ]}
          />
        ) : (
          <WidgetStat
            value={q.data.active}
            href='/patients'
            caption='Patients with a procedure in progress'
            trend={
              <div className='flex items-center gap-2' title={`${share}% of all patients`}>
                <div className='h-1.5 flex-1 rounded-full bg-blue-100'>
                  <div className='h-1.5 rounded-full bg-blue-600' style={{ width: `${share}%` }} />
                </div>
                <span className='text-[11px] font-medium tabular-nums text-gray-600'>{share}%</span>
              </div>
            }
            footer={`of ${q.data.total} patients`}
          />
        ))}
    </WidgetQueryState>
  )
}

export function FormsAwaitingReviewWidget({ time, view }: WidgetBodyProps) {
  const q = useFormsSummary(time.query)
  return (
    <WidgetQueryState isLoading={q.isLoading} error={q.error} isEmpty={false} onRetry={() => void q.refetch()}>
      {q.data &&
        (view === 'number' ? (
          <WidgetStat
            value={q.data.awaitingReview}
            href='/submissions'
            tone={q.data.awaitingReview > 0 ? 'amber' : 'blue'}
            caption='Forms waiting for a review signature'
            trend={<Sparkline points={q.data.completedByDay} label='forms completed' color={STATUS_COLORS.good} />}
            footer={`${q.data.completedInPeriod} forms completed · ${windowLabel(time.window)}`}
          />
        ) : (
          <div className='flex flex-1 flex-col gap-1'>
            <p className='text-xs text-gray-500'>
              <span className='font-semibold text-amber-600'>{q.data.awaitingReview} waiting for review</span> ·{' '}
              {q.data.completedInPeriod} completed
            </p>
            <SeriesView
              view={seriesKind(view, 'columns')}
              rows={dailyRows(q.data.completedByDay)}
              series={[{ key: 'count', label: 'Forms completed', color: STATUS_COLORS.good }]}
            />
          </div>
        ))}
    </WidgetQueryState>
  )
}

function RunRow({ run, aside }: { run: DashboardRunRef; aside: React.ReactNode }) {
  const href = runHref(run)
  const body = (
    <>
      <span className='h-2 w-2 shrink-0 rounded-full' style={{ backgroundColor: typeColor(run.type) }} aria-hidden />
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-xs font-medium text-gray-800'>{runTitle(run)}</span>
        <span className='block truncate text-[11px] text-gray-400'>
          {procedureTypeLabel(run.type)}
          {run.patientMrn && run.type === 'Transplant' ? ` · ${run.patientMrn}` : ''}
        </span>
      </span>
      <span className='shrink-0 text-[11px] text-gray-500'>{aside}</span>
    </>
  )
  return (
    <li>
      {href ? (
        <Link to={href} className='-mx-1 flex items-center gap-2 rounded px-1 py-1.5 hover:bg-gray-50'>
          {body}
        </Link>
      ) : (
        <div className='flex items-center gap-2 py-1.5'>{body}</div>
      )}
    </li>
  )
}
